import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getFFTAnalysis, getAlarms } from '../../services/bobAssistantService';

const FFTChart = ({ deviceId }) => {
    const [fftData, setFftData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [availableAlarms, setAvailableAlarms] = useState([]);
    const [selectedAlarmId, setSelectedAlarmId] = useState(null);

    useEffect(() => {
        loadAlarmsWithFFT();
    }, [deviceId]);

    useEffect(() => {
        if (selectedAlarmId !== null) {
            loadFFTData(selectedAlarmId);
        }
    }, [selectedAlarmId]);

    const loadAlarmsWithFFT = async () => {
        try {
            setLoading(true);
            // Cargar alarmas recientes
            const alarms = await getAlarms(deviceId, { days: 90, limit: 20 });
            const alarmsWithFFT = alarms.filter(a => a.fft_data);
            setAvailableAlarms(alarmsWithFFT);

            // Si hay alarmas con FFT, cargar la más reciente
            if (alarmsWithFFT.length > 0) {
                setSelectedAlarmId(alarmsWithFFT[0].id);
            } else {
                // Intentar cargar cualquier FFT disponible
                const fft = await getFFTAnalysis(deviceId);
                if (fft.has_fft) {
                    setFftData(fft);
                }
                setLoading(false);
            }
        } catch (err) {
            console.error('Error loading FFT data:', err);
            setLoading(false);
        }
    };

    const loadFFTData = async (alarmId) => {
        try {
            setLoading(true);
            const data = await getFFTAnalysis(deviceId, alarmId);
            setFftData(data);
        } catch (err) {
            console.error('Error loading FFT data:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleString('en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="bob-fft-container">
                <h4>FFT Analysis</h4>
                <p>Loading FFT data...</p>
            </div>
        );
    }

    if (!fftData || !fftData.has_fft) {
        return (
            <div className="bob-fft-container">
                <h4>FFT Analysis</h4>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '40px' }}>
                    No FFT data available. FFT data is generated when an alarm occurs.
                </p>
            </div>
        );
    }

    return (
        <div className="bob-fft-container">
            <h4>Frequency Analysis (FFT)</h4>

            {/* Selector de alarma */}
            {availableAlarms.length > 1 && (
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginRight: '8px' }}>
                        Select alarm:
                    </label>
                    <select
                        value={selectedAlarmId || ''}
                        onChange={(e) => setSelectedAlarmId(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '13px'
                        }}
                    >
                        {availableAlarms.map(alarm => (
                            <option key={alarm.id} value={alarm.id}>
                                {formatDate(alarm.timestamp)} - Anomaly {alarm.anomaly_level}%
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Información de la alarma */}
            <div className="bob-fft-info">
                <span>Date: {formatDate(fftData.alarm_timestamp)}</span>
                <span>Anomaly: {fftData.anomaly_level}%</span>
            </div>

            {/* Gráfico de barras FFT */}
            <ResponsiveContainer width="100%" height={350}>
                <BarChart data={fftData.fft_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                        dataKey="bin"
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fontSize: 10 }}
                        label={{
                            value: 'Frequency Bin',
                            position: 'insideBottom',
                            offset: -5,
                            fill: 'rgba(255,255,255,0.5)',
                            fontSize: 12
                        }}
                    />
                    <YAxis
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fontSize: 10 }}
                        label={{
                            value: 'Amplitude',
                            angle: -90,
                            position: 'insideLeft',
                            fill: 'rgba(255,255,255,0.5)',
                            fontSize: 12
                        }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#1a1a2e',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name, props) => [
                            `${value?.toFixed(4)}`,
                            `Amplitude (${props.payload.frequency_range})`
                        ]}
                    />
                    <Bar
                        dataKey="amplitude"
                        fill="#6366f1"
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>

            {/* Descripción */}
            <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.6)'
            }}>
                <strong>Interpretation:</strong> FFT (Fast Fourier Transform) analysis
                shows the distribution of vibration frequencies. High peaks at certain
                frequencies may indicate specific problems such as imbalance, misalignment,
                or bearing wear.
            </div>
        </div>
    );
};

export default FFTChart;
